import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddPaymentAcceptanceConfigurationRulesOptions,
  AddPaymentAcceptanceConfigurationRulesResponse,
  BulkUpdatePaymentAcceptanceConfigurationTagsByFilterOptions,
  BulkUpdatePaymentAcceptanceConfigurationTagsByFilterResponse,
  BulkUpdatePaymentAcceptanceConfigurationTagsOptions,
  BulkUpdatePaymentAcceptanceConfigurationTagsResponse,
  ModifyPaymentAcceptanceConfigurationRulesOptions,
  ModifyPaymentAcceptanceConfigurationRulesResponse,
  PaymentAcceptanceConfiguration,
  PaymentAcceptanceConfigurationQuery,
  PaymentAcceptanceConfigurationsQueryBuilder,
  QueryPaymentAcceptanceConfigurationsResponse,
  RemovePaymentAcceptanceConfigurationRulesOptions,
  RemovePaymentAcceptanceConfigurationRulesResponse,
  UpdatePaymentAcceptanceConfiguration,
  addPaymentAcceptanceConfigurationRules as universalAddPaymentAcceptanceConfigurationRules,
  bulkUpdatePaymentAcceptanceConfigurationTags as universalBulkUpdatePaymentAcceptanceConfigurationTags,
  bulkUpdatePaymentAcceptanceConfigurationTagsByFilter as universalBulkUpdatePaymentAcceptanceConfigurationTagsByFilter,
  createPaymentAcceptanceConfiguration as universalCreatePaymentAcceptanceConfiguration,
  deletePaymentAcceptanceConfiguration as universalDeletePaymentAcceptanceConfiguration,
  getPaymentAcceptanceConfiguration as universalGetPaymentAcceptanceConfiguration,
  modifyPaymentAcceptanceConfigurationRules as universalModifyPaymentAcceptanceConfigurationRules,
  queryPaymentAcceptanceConfigurations as universalQueryPaymentAcceptanceConfigurations,
  removePaymentAcceptanceConfigurationRules as universalRemovePaymentAcceptanceConfigurationRules,
  typedQueryPaymentAcceptanceConfigurations as universalTypedQueryPaymentAcceptanceConfigurations,
  updatePaymentAcceptanceConfiguration as universalUpdatePaymentAcceptanceConfiguration,
} from './payments-payment-acceptance-configurations-v1-payment-acceptance-configuration-payment-acceptance-configurations.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

/** @internal */
export function createPaymentAcceptanceConfiguration(
  httpClient: HttpClient
): CreatePaymentAcceptanceConfigurationSignature {
  return (paymentAcceptanceConfiguration: PaymentAcceptanceConfiguration) =>
    universalCreatePaymentAcceptanceConfiguration(
      paymentAcceptanceConfiguration,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePaymentAcceptanceConfigurationSignature {
  /**
   * Creates a PaymentAcceptanceConfiguration.
   * @param - PaymentAcceptanceConfiguration to be created.
   * @returns The created PaymentAcceptanceConfiguration.
   */
  (paymentAcceptanceConfiguration: PaymentAcceptanceConfiguration): Promise<
    NonNullablePaths<
      PaymentAcceptanceConfiguration,
      | `rules`
      | `rules.${number}.paymentMethodTypeId`
      | `rules.${number}.accountConnectionId`
      | `rules.${number}.merchantEnabled`
      | `tags.tags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function getPaymentAcceptanceConfiguration(
  httpClient: HttpClient
): GetPaymentAcceptanceConfigurationSignature {
  return (paymentAcceptanceConfigurationId: string) =>
    universalGetPaymentAcceptanceConfiguration(
      paymentAcceptanceConfigurationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPaymentAcceptanceConfigurationSignature {
  /**
   * Retrieves a PaymentAcceptanceConfiguration.
   * @param - ID of the PaymentAcceptanceConfiguration to retrieve.
   * @returns The requested PaymentAcceptanceConfiguration.
   */
  (paymentAcceptanceConfigurationId: string): Promise<
    NonNullablePaths<
      PaymentAcceptanceConfiguration,
      | `rules`
      | `rules.${number}.paymentMethodTypeId`
      | `rules.${number}.accountConnectionId`
      | `rules.${number}.merchantEnabled`
      | `tags.tags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function updatePaymentAcceptanceConfiguration(
  httpClient: HttpClient
): UpdatePaymentAcceptanceConfigurationSignature {
  return (
    _id: string,
    paymentAcceptanceConfiguration: NonNullablePaths<
      UpdatePaymentAcceptanceConfiguration,
      `revision`,
      2
    >
  ) =>
    universalUpdatePaymentAcceptanceConfiguration(
      _id,
      paymentAcceptanceConfiguration,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePaymentAcceptanceConfigurationSignature {
  /**
   * Updates a PaymentAcceptanceConfiguration.
   *
   *
   * Each time the PaymentAcceptanceConfiguration is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the PaymentAcceptanceConfiguration.
   * This ensures you're working with the latest PaymentAcceptanceConfiguration
   * and prevents unintended overwrites.
   * @param - Id
   * @returns Updated PaymentAcceptanceConfiguration.
   */
  (
    _id: string,
    paymentAcceptanceConfiguration: NonNullablePaths<
      UpdatePaymentAcceptanceConfiguration,
      `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      PaymentAcceptanceConfiguration,
      | `rules`
      | `rules.${number}.paymentMethodTypeId`
      | `rules.${number}.accountConnectionId`
      | `rules.${number}.merchantEnabled`
      | `tags.tags.tagIds`,
      4
    >
  >;
}

/** @internal */
export function addPaymentAcceptanceConfigurationRules(
  httpClient: HttpClient
): AddPaymentAcceptanceConfigurationRulesSignature {
  return (
    paymentAcceptanceConfigurationId: string,
    options?: NonNullablePaths<
      AddPaymentAcceptanceConfigurationRulesOptions,
      `rules`,
      2
    >
  ) =>
    universalAddPaymentAcceptanceConfigurationRules(
      paymentAcceptanceConfigurationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddPaymentAcceptanceConfigurationRulesSignature {
  /**
   * Add PaymentAcceptanceConfiguration Rules
   * as result new rules will be validated and added to existing ones
   * @param - Id of payment acceptance configuration to update
   */
  (
    paymentAcceptanceConfigurationId: string,
    options?: NonNullablePaths<
      AddPaymentAcceptanceConfigurationRulesOptions,
      `rules`,
      2
    >
  ): Promise<
    NonNullablePaths<
      AddPaymentAcceptanceConfigurationRulesResponse,
      | `paymentAcceptanceConfiguration.rules`
      | `paymentAcceptanceConfiguration.rules.${number}.paymentMethodTypeId`
      | `paymentAcceptanceConfiguration.rules.${number}.accountConnectionId`
      | `paymentAcceptanceConfiguration.rules.${number}.merchantEnabled`
      | `paymentAcceptanceConfiguration.tags.tags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function removePaymentAcceptanceConfigurationRules(
  httpClient: HttpClient
): RemovePaymentAcceptanceConfigurationRulesSignature {
  return (
    paymentAcceptanceConfigurationId: string,
    options?: NonNullablePaths<
      RemovePaymentAcceptanceConfigurationRulesOptions,
      `ruleIds`,
      2
    >
  ) =>
    universalRemovePaymentAcceptanceConfigurationRules(
      paymentAcceptanceConfigurationId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemovePaymentAcceptanceConfigurationRulesSignature {
  /**
   * Remove PaymentAcceptanceConfiguration
   * as result existing rules will be removed from PaymentAcceptanceConfiguration
   * some extra rules may be deleted
   * @param - Id of payment acceptance configuration to update
   */
  (
    paymentAcceptanceConfigurationId: string,
    options?: NonNullablePaths<
      RemovePaymentAcceptanceConfigurationRulesOptions,
      `ruleIds`,
      2
    >
  ): Promise<
    NonNullablePaths<
      RemovePaymentAcceptanceConfigurationRulesResponse,
      | `paymentAcceptanceConfiguration.rules`
      | `paymentAcceptanceConfiguration.rules.${number}.paymentMethodTypeId`
      | `paymentAcceptanceConfiguration.rules.${number}.accountConnectionId`
      | `paymentAcceptanceConfiguration.rules.${number}.merchantEnabled`
      | `paymentAcceptanceConfiguration.tags.tags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function modifyPaymentAcceptanceConfigurationRules(
  httpClient: HttpClient
): ModifyPaymentAcceptanceConfigurationRulesSignature {
  return (
    paymentAcceptanceConfigurationId: string,
    paymentAcceptanceConfigurationRevision: string,
    options?: ModifyPaymentAcceptanceConfigurationRulesOptions
  ) =>
    universalModifyPaymentAcceptanceConfigurationRules(
      paymentAcceptanceConfigurationId,
      paymentAcceptanceConfigurationRevision,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ModifyPaymentAcceptanceConfigurationRulesSignature {
  /**
   * Modify PaymentAcceptanceConfiguration Rules
   * as result new rules will be validated and added to existing ones
   * rules to removed will be removed, may remove extra rules depending on app_id specific logic
   * some extra rules may be added
   * rules_to_update allows partial updates to existing rules using field masks
   * @param - Id of payment acceptance configuration to update
   * @param - Payment acceptance configuration revision
   */
  (
    paymentAcceptanceConfigurationId: string,
    paymentAcceptanceConfigurationRevision: string,
    options?: ModifyPaymentAcceptanceConfigurationRulesOptions
  ): Promise<
    NonNullablePaths<
      ModifyPaymentAcceptanceConfigurationRulesResponse,
      | `paymentAcceptanceConfiguration.rules`
      | `paymentAcceptanceConfiguration.rules.${number}.paymentMethodTypeId`
      | `paymentAcceptanceConfiguration.rules.${number}.accountConnectionId`
      | `paymentAcceptanceConfiguration.rules.${number}.merchantEnabled`
      | `paymentAcceptanceConfiguration.tags.tags.tagIds`,
      5
    >
  >;
}

/** @internal */
export function deletePaymentAcceptanceConfiguration(
  httpClient: HttpClient
): DeletePaymentAcceptanceConfigurationSignature {
  return (paymentAcceptanceConfigurationId: string) =>
    universalDeletePaymentAcceptanceConfiguration(
      paymentAcceptanceConfigurationId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeletePaymentAcceptanceConfigurationSignature {
  /**
   * Delete a PaymentAcceptanceConfiguration
   * @param - Id of payment acceptance configuration to delete
   */
  (paymentAcceptanceConfigurationId: string): Promise<void>;
}

export function queryPaymentAcceptanceConfigurations(
  httpClient: HttpClient
): QueryPaymentAcceptanceConfigurationsSignature {
  return () =>
    universalQueryPaymentAcceptanceConfigurations(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPaymentAcceptanceConfigurationsSignature {
  /**
   * Retrieves a list of PaymentAcceptanceConfigurations, given the provided [paging, filtering, and sorting][1].
   *
   * Up to 1,000 PaymentAcceptanceConfigurations can be returned per request.
   *
   * To learn how to query PaymentAcceptanceConfigurations, see [API Query Language][2].
   *
   * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
   * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
   */
  (): PaymentAcceptanceConfigurationsQueryBuilder;
}

export function typedQueryPaymentAcceptanceConfigurations(
  httpClient: HttpClient
): TypedQueryPaymentAcceptanceConfigurationsSignature {
  return (query: PaymentAcceptanceConfigurationQuery) =>
    universalTypedQueryPaymentAcceptanceConfigurations(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPaymentAcceptanceConfigurationsSignature {
  /** */
  (query: PaymentAcceptanceConfigurationQuery): Promise<
    NonNullablePaths<
      QueryPaymentAcceptanceConfigurationsResponse,
      `paymentAcceptanceConfigurations`,
      2
    >
  >;
}

/** @internal */
export function bulkUpdatePaymentAcceptanceConfigurationTags(
  httpClient: HttpClient
): BulkUpdatePaymentAcceptanceConfigurationTagsSignature {
  return (
    ids: string[],
    options?: BulkUpdatePaymentAcceptanceConfigurationTagsOptions
  ) =>
    universalBulkUpdatePaymentAcceptanceConfigurationTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePaymentAcceptanceConfigurationTagsSignature {
  /**
   * Synchronously update tags on multiple PaymentAcceptanceConfigurations, by list of PaymentAcceptanceConfigurations ids
   * A tag that appears both in the list of assign and unassign tags, will be assigned
   * @param - List of NileProtoTagsEntities that their tags will update.
   */
  (
    ids: string[],
    options?: BulkUpdatePaymentAcceptanceConfigurationTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdatePaymentAcceptanceConfigurationTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

/** @internal */
export function bulkUpdatePaymentAcceptanceConfigurationTagsByFilter(
  httpClient: HttpClient
): BulkUpdatePaymentAcceptanceConfigurationTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdatePaymentAcceptanceConfigurationTagsByFilterOptions
  ) =>
    universalBulkUpdatePaymentAcceptanceConfigurationTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePaymentAcceptanceConfigurationTagsByFilterSignature {
  /**
   * Asynchronously update tags on multiple PaymentAcceptanceConfigurations, by provided filter
   * An empty filter will update all PaymentAcceptanceConfigurations
   * A tag that appears both in the list of assign and unassign tags, will be assigned
   * @param - Filter
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdatePaymentAcceptanceConfigurationTagsByFilterOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdatePaymentAcceptanceConfigurationTagsByFilterResponse,
      `jobId`,
      2
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddPaymentAcceptanceConfigurationRulesOptions,
  AddPaymentAcceptanceConfigurationRulesRequest,
  AddPaymentAcceptanceConfigurationRulesResponse,
  ApplicationError,
  Asset,
  BulkActionMetadata,
  BulkUpdatePaymentAcceptanceConfigurationTagsByFilterOptions,
  BulkUpdatePaymentAcceptanceConfigurationTagsByFilterRequest,
  BulkUpdatePaymentAcceptanceConfigurationTagsByFilterResponse,
  BulkUpdatePaymentAcceptanceConfigurationTagsOptions,
  BulkUpdatePaymentAcceptanceConfigurationTagsRequest,
  BulkUpdatePaymentAcceptanceConfigurationTagsResponse,
  BulkUpdatePaymentAcceptanceConfigurationTagsResult,
  Countries,
  CreatePaymentAcceptanceConfigurationRequest,
  CreatePaymentAcceptanceConfigurationResponse,
  Currencies,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteContext,
  DeletePaymentAcceptanceConfigurationRequest,
  DeletePaymentAcceptanceConfigurationResponse,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetPaymentAcceptanceConfigurationRequest,
  GetPaymentAcceptanceConfigurationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MaskedRules,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  ModifyPaymentAcceptanceConfigurationRulesOptions,
  ModifyPaymentAcceptanceConfigurationRulesRequest,
  ModifyPaymentAcceptanceConfigurationRulesResponse,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PaymentAcceptanceConfiguration,
  PaymentAcceptanceConfigurationQuerySpec,
  PaymentAcceptanceConfigurationsQueryBuilder,
  PaymentAcceptanceConfigurationsQueryResult,
  PaymentAcceptanceRule,
  PaymentBrands,
  PicassoAssigned,
  PicassoUnassigned,
  QueryPaymentAcceptanceConfigurationsRequest,
  QueryPaymentAcceptanceConfigurationsResponse,
  RemovePaymentAcceptanceConfigurationRulesOptions,
  RemovePaymentAcceptanceConfigurationRulesRequest,
  RemovePaymentAcceptanceConfigurationRulesResponse,
  RestoreInfo,
  Restrictions,
  RestrictionsCountriesOneOf,
  RestrictionsCurrenciesOneOf,
  RestrictionsPaymentBrandsOneOf,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  TagList,
  Tags,
  UpdatePaymentAcceptanceConfiguration,
  UpdatePaymentAcceptanceConfigurationRequest,
  UpdatePaymentAcceptanceConfigurationResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './payments-payment-acceptance-configurations-v1-payment-acceptance-configuration-payment-acceptance-configurations.universal.js';
